import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  Drawer,
  Grid,
  IconButton,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  Divider,
  Tooltip,
  Collapse,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import ClientSelect from '../../../components/common/ClientSelect';
import serviceTemplateService from '../../services/services/serviceTemplateService';
import serviceService from '../../services/services/serviceService';
import quotePackageService from '../services/quotePackageService';
import quoteService from '../services/quoteService';
import ProjectSelect from '../../../components/common/ProjectSelect';
import ClientService from '../../clients/services/clientService';
import ProjectService from '../../projects/services/projectService';
import { logger } from '../../../utils/logger';

const steps = ['Βασικά στοιχεία', 'Υπηρεσίες', 'Επιβεβαίωση'];
const PAGE_SIZE = 25;
const billingPeriodLabels = {
  one_time: 'Εφάπαξ',
  monthly: 'Μηνιαία',
  quarterly: 'Τριμηνιαία',
  semi_annual: 'Εξαμηνιαία',
  annual: 'Ετήσια',
  biennial: 'Διετής',
};
const serviceTypeLabels = {
  development: 'Development',
  hosting: 'Hosting',
  maintenance: 'Maintenance',
  saas: 'SaaS',
  seo: 'SEO',
  design: 'Design',
  support: 'Support',
  marketing: 'Marketing',
  consulting: 'Consulting',
  other: 'Άλλο',
};

const formatServiceTypeLabel = (type) => {
  if (!type) return '';
  const key = String(type).toLowerCase();
  if (serviceTypeLabels[key]) {
    return serviceTypeLabels[key];
  }
  return key
    .split(/[_\s-]+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const shouldPrefixForPeriod = (billingPeriod) => {
  if (!billingPeriod) return false;
  const normalized = String(billingPeriod).toLowerCase();
  return normalized !== 'one_time' && normalized !== 'one-time';
};

const buildPrefixedName = (name, serviceType, billingPeriod) => {
  const baseName = name || '';
  const typeLabel = formatServiceTypeLabel(serviceType);
  if (!shouldPrefixForPeriod(billingPeriod) || !typeLabel) {
    return baseName;
  }
  const lowerBase = baseName.toLowerCase();
  const lowerLabel = `${typeLabel.toLowerCase()} -`;
  if (lowerBase.startsWith(lowerLabel)) {
    return baseName;
  }
  return `${typeLabel} - ${baseName}`;
};

const initialCompanyInfo = {
  company_name: '',
  full_name: '',
  email: '',
  phone: '',
  website: '',
  bank_name: '',
  iban: '',
  account_holder: '',
  payment_terms: 40,
  delivery_days: 20,
  validity_months: 2,
};

const defaultManualItem = () => ({
  name: '',
  description: '',
  features: [],
  quantity: 1,
  unit_price: 0,
  vat_rate: 24,
  billing_period: 'one_time',
});

const QuoteFormDrawer = ({ open, onClose, onSubmit, quote: initialQuote }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [expandedCompanyInfo, setExpandedCompanyInfo] = useState(false);

  const [formData, setFormData] = useState({
    client_id: '',
    project_id: '',
    package_id: '',
    title: '',
    description: '',
    logo_url: '',
    payment_terms: 40,
    delivery_days: 20,
    validity_months: 2,
    company_info: initialCompanyInfo,
    services: [],
    manualItems: [defaultManualItem()],
  });

  const [packages, setPackages] = useState([]);
  const [isLoadingPackages, setIsLoadingPackages] = useState(false);
  const [packageSearch, setPackageSearch] = useState('');

  const [serviceSearch, setServiceSearch] = useState('');
  const [serviceOptions, setServiceOptions] = useState([]);
  const [isLoadingServices, setIsLoadingServices] = useState(false);

  const [validationErrors, setValidationErrors] = useState({});
  const [clientServiceSearch, setClientServiceSearch] = useState('');
  const [clientServiceOptions, setClientServiceOptions] = useState([]);
  const [isLoadingClientServices, setIsLoadingClientServices] = useState(false);
  const [sectionsExpanded, setSectionsExpanded] = useState({
    templates: true,
    clientServices: false,
    manual: true,
    selected: true,
  });
  const prevClientIdRef = useRef(null);
  const [reviewData, setReviewData] = useState({
    clientName: null,
    projectName: null,
    packageName: null,
  });

  const totals = useMemo(() => {
    const serviceTotal = formData.services.reduce((acc, service) => {
      const quantity = Number(service.quantity || 0);
      const price = Number(service.unit_price || 0);
      const vat = Number(service.vat_rate || 0);
      const lineSubtotal = quantity * price;
      const lineVat = lineSubtotal * (vat / 100);
      return {
        subtotal: acc.subtotal + lineSubtotal,
        vat: acc.vat + lineVat,
      };
    }, { subtotal: 0, vat: 0 });

    const manualTotal = formData.manualItems.reduce((acc, item) => {
      if (!item.name) {
        return acc;
      }
      const quantity = Number(item.quantity || 0);
      const price = Number(item.unit_price || 0);
      const vat = Number(item.vat_rate || 0);
      const lineSubtotal = quantity * price;
      const lineVat = lineSubtotal * (vat / 100);
      return {
        subtotal: acc.subtotal + lineSubtotal,
        vat: acc.vat + lineVat,
      };
    }, { subtotal: 0, vat: 0 });

    const subtotal = serviceTotal.subtotal + manualTotal.subtotal;
    const vat = serviceTotal.vat + manualTotal.vat;
    const total = subtotal + vat;

    return {
      subtotal: quoteService.formatCurrency(subtotal),
      vat: quoteService.formatCurrency(vat),
      total: quoteService.formatCurrency(total),
    };
  }, [formData.services, formData.manualItems]);
  const selectedCounts = useMemo(() => {
    const manualNamed = formData.manualItems.filter((item) => item.name);
    const clientSelections = manualNamed.filter((item) => item.client_service_id).length;
    const customLines = Math.max(manualNamed.length - clientSelections, 0);
    return {
      templates: formData.services.length,
      client: clientSelections,
      custom: customLines,
    };
  }, [formData.services, formData.manualItems]);

  const selectedServices = useMemo(() => {
    const templateEntries = formData.services.map((service, index) => ({
      key: `template-${service.template_id ?? index}`,
      type: 'template',
      index,
      name: service.name || `Υπηρεσία ${index + 1}`,
      description: service.description,
      features: service.features || [],
      quantity: Number(service.quantity || 0),
      unitPrice: Number(service.unit_price || 0),
      billingPeriod: service.billing_period,
      sourceLabel: 'Πρότυπο υπηρεσίας',
      serviceTypeLabel: formatServiceTypeLabel(service.service_type),
    }));

    const manualEntries = formData.manualItems
      .map((item, index) => ({
        key: `manual-${index}`,
        type: item.client_service_id ? 'client' : 'manual',
        index,
        name: item.name,
        description: item.description,
        features: item.features || [],
        quantity: Number(item.quantity || 0),
        unitPrice: Number(item.unit_price || 0),
        billingPeriod: item.billing_period,
        sourceLabel: item.client_service_id ? 'Υπηρεσία πελάτη' : 'Προσαρμοσμένη γραμμή',
        serviceTypeLabel: formatServiceTypeLabel(item.service_type),
      }))
      .filter((entry) => entry.name);

    return [...templateEntries, ...manualEntries];
  }, [formData.services, formData.manualItems]);

  const selectedClientServicesCount = selectedCounts.client;
  const handleToggleSection = (section) => (event, isExpanded) => {
    setSectionsExpanded((prev) => ({
      ...prev,
      [section]: isExpanded,
    }));
  };
  const updateManualItems = useCallback((updater) => {
    setFormData((prev) => {
      const nextManualItems = updater(prev.manualItems);
      const normalizedItems = nextManualItems.length > 0 ? nextManualItems : [defaultManualItem()];
      return {
        ...prev,
        manualItems: normalizedItems,
      };
    });
  }, []);

  // Load quote data when editing
  useEffect(() => {
    if (open && initialQuote) {
      // Separate quote_items into services (with template_id) and manualItems (without template_id)
      const servicesFromQuote = [];
      const manualItemsFromQuote = [];
      
      if (initialQuote.quote_items && initialQuote.quote_items.length > 0) {
        // Sort items by sort_order to maintain the correct order
        const sortedItems = [...initialQuote.quote_items].sort((a, b) => 
          (a.sort_order || 0) - (b.sort_order || 0)
        );
        
        sortedItems.forEach((item) => {
          if (item.template_id) {
            // This is a service from template
            servicesFromQuote.push({
              template_id: item.template_id,
              name: item.name || '',
              description: item.description || '',
              features: item.features || [],
              quantity: Number(item.quantity || 1),
              unit_price: Number(item.unit_price || 0),
              vat_rate: Number(item.vat_rate || 24),
              billing_period: item.billing_period || 'one_time',
            });
          } else {
            // This is a manual item
            manualItemsFromQuote.push({
              template_id: null,
              name: item.name || '',
              description: item.description || '',
              features: item.features || [],
              quantity: Number(item.quantity || 1),
              unit_price: Number(item.unit_price || 0),
              vat_rate: Number(item.vat_rate || 24),
              billing_period: item.billing_period || 'one_time',
            });
          }
        });
      }

      const quoteSettings = initialQuote.quote_settings || {};
      const companyInfo = initialQuote.company_info || initialCompanyInfo;
      
      setFormData({
        client_id: initialQuote.client_id?.toString() || '',
        project_id: initialQuote.project_id?.toString() || '',
        package_id: initialQuote.package_id?.toString() || '',
        title: initialQuote.title || '',
        description: initialQuote.description || '',
        logo_url: initialQuote.logo_url || '',
        payment_terms: quoteSettings.payment_terms || companyInfo.payment_terms || 40,
        delivery_days: quoteSettings.delivery_days || companyInfo.delivery_days || 20,
        validity_months: quoteSettings.validity_months || companyInfo.validity_months || 2,
        company_info: companyInfo,
        services: servicesFromQuote,
        manualItems: manualItemsFromQuote.length > 0 ? manualItemsFromQuote : [defaultManualItem()],
      });
    }
  }, [open, initialQuote]);

  // Reset form when drawer closes
  useEffect(() => {
    if (!open && !initialQuote) {
      setActiveStep(0);
      setErrorMessage('');
      setValidationErrors({});
      setFormData({
        client_id: '',
        project_id: '',
        package_id: '',
        title: '',
        description: '',
        logo_url: '',
        payment_terms: 40,
        delivery_days: 20,
        validity_months: 2,
        company_info: initialCompanyInfo,
        services: [],
        manualItems: [defaultManualItem()],
      });
      setServiceOptions([]);
      setPackageSearch('');
      setServiceSearch('');
      setClientServiceOptions([]);
      setClientServiceSearch('');
      setIsLoadingClientServices(false);
      setExpandedCompanyInfo(false);
      setSectionsExpanded({
        templates: true,
        clientServices: false,
        manual: true,
        selected: true,
      });
      prevClientIdRef.current = null;
    }
  }, [open, initialQuote]);

  // Load packages on open or search change
  const loadPackages = useCallback(async (searchValue = '') => {
    setIsLoadingPackages(true);
    const params = {
      skip: 0,
      limit: PAGE_SIZE,
      active_only: true,
    };
    if (searchValue) {
      params.search = searchValue;
    }
    const response = await quotePackageService.getAll(params);
    if (response.success) {
      setPackages(response.data || []);
    } else {
      setPackages([]);
    }
    setIsLoadingPackages(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    loadPackages(packageSearch);
  }, [open, packageSearch, loadPackages]);

  // Load service templates on search
  useEffect(() => {
    if (!open) return;
    let isActive = true;
    const fetchServices = async () => {
      setIsLoadingServices(true);
      const params = {
        skip: 0,
        limit: PAGE_SIZE,
        active_only: true,
      };
      if (serviceSearch) {
        params.search = serviceSearch;
      }
      const result = await serviceTemplateService.getAll(params);
      if (isActive) {
        if (result.success) {
          setServiceOptions(result.data || []);
        } else {
          setServiceOptions([]);
        }
      }
      setIsLoadingServices(false);
    };
    fetchServices();
    return () => {
      isActive = false;
    };
  }, [open, serviceSearch]);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (!formData.client_id) {
      setClientServiceOptions([]);
      setIsLoadingClientServices(false);
      return;
    }

    let isActive = true;

    const fetchClientServices = async () => {
      setIsLoadingClientServices(true);
      const params = {
        skip: 0,
        limit: PAGE_SIZE,
        client_id: formData.client_id,
        status: 'active',
      };
      if (clientServiceSearch) {
        params.search = clientServiceSearch;
      }
      const response = await serviceService.getAll(params);
      if (isActive) {
        if (response.success) {
          setClientServiceOptions(response.data || []);
        } else {
          setClientServiceOptions([]);
        }
        setIsLoadingClientServices(false);
      }
    };

    fetchClientServices();

    return () => {
      isActive = false;
    };
  }, [open, formData.client_id, clientServiceSearch]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const previousClientId = prevClientIdRef.current;
    if (previousClientId && previousClientId !== formData.client_id) {
      updateManualItems((manualItems) => manualItems.filter((item) => !item.client_service_id));
      setClientServiceSearch('');
    }
    prevClientIdRef.current = formData.client_id || null;
  }, [formData.client_id, open, updateManualItems]);

  // Load review data when entering review step
  useEffect(() => {
    if (activeStep === 2 && open) {
      const loadReviewData = async () => {
        const newReviewData = {
          clientName: null,
          projectName: null,
          packageName: null,
        };

        // Load client name
        if (formData.client_id) {
          try {
            const clientResult = await ClientService.getById(formData.client_id);
            if (clientResult.success && clientResult.data) {
              newReviewData.clientName = clientResult.data.name || clientResult.data.business_name || '—';
            }
          } catch (err) {
            logger.error('Error loading client for review:', err);
          }
        } else if (formData.company_info?.full_name) {
          newReviewData.clientName = formData.company_info.full_name;
        }

        // Load project name
        if (formData.project_id) {
          try {
            const projectResult = await ProjectService.getById(formData.project_id);
            if (projectResult.success && projectResult.data) {
              newReviewData.projectName = projectResult.data.name || '—';
            }
          } catch (err) {
            logger.error('Error loading project for review:', err);
          }
        }

        // Load package name (from already loaded packages)
        if (formData.package_id) {
          const selectedPackage = packages.find((pkg) => String(pkg.id) === String(formData.package_id));
          if (selectedPackage) {
            newReviewData.packageName = selectedPackage.name || '—';
          }
        }

        setReviewData(newReviewData);
      };

      loadReviewData();
    }
  }, [activeStep, open, formData.client_id, formData.project_id, formData.package_id, formData.company_info?.full_name, packages]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCompanyInfoChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      company_info: {
        ...prev.company_info,
        [field]: value,
      },
    }));
  };

  const handleManualItemChange = (index, field, value) => {
    updateManualItems((manualItems) => {
      if (!manualItems[index]) {
        return manualItems;
      }
      const nextItems = [...manualItems];
      nextItems[index] = {
        ...nextItems[index],
        [field]:
          field === 'features'
            ? typeof value === 'string'
              ? value.split('\n').map((line) => line.trim()).filter(Boolean)
              : []
            : value,
      };
      return nextItems;
    });
  };

  const addManualItem = () => {
    updateManualItems((manualItems) => [...manualItems, defaultManualItem()]);
  };

  const removeManualItem = (index) => {
    updateManualItems((manualItems) => manualItems.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleAddService = (template) => {
    if (!template) return;
    const exists = formData.services.some((service) => service.template_id === template.id);
    if (exists) {
      setErrorMessage('Η υπηρεσία υπάρχει ήδη στη λίστα');
      setTimeout(() => setErrorMessage(''), 2500);
      return;
    }

    const billingPeriod = template.billing_period || 'one_time';
    const prefixedName = buildPrefixedName(template.name, template.service_type, billingPeriod);
    const newService = {
      template_id: template.id,
      name: prefixedName,
      description: template.description || '',
      features: Array.isArray(template.features) ? template.features : [],
      quantity: 1,
      unit_price: Number(template.default_price || 0),
      vat_rate: Number(template.vat_rate || 24),
      billing_period: billingPeriod,
      sort_order: formData.services.length,
      service_type: template.service_type || null,
    };

    setFormData((prev) => ({
      ...prev,
      services: [...prev.services, newService],
    }));
  };

  const handleServiceChange = (index, field, value) => {
    setFormData((prev) => {
      const services = [...prev.services];
      const updateValue = field === 'features'
        ? value.split('\n').map((line) => line.trim()).filter(Boolean)
        : value;
      services[index] = {
        ...services[index],
        [field]: updateValue,
      };
      return { ...prev, services };
    });
  };

  const removeService = (index) => {
    setFormData((prev) => {
      const services = [...prev.services];
      services.splice(index, 1);
      return { ...prev, services };
    });
  };

  const handleAddClientService = (service) => {
    if (!service) return;
    updateManualItems((manualItems) => {
      const alreadySelected = manualItems.some((item) => item.client_service_id === service.id);
      if (alreadySelected) {
        setErrorMessage('Η υπηρεσία πελάτη υπάρχει ήδη στη λίστα');
        setTimeout(() => setErrorMessage(''), 2500);
        return manualItems;
      }

      const nextItems = [
        ...manualItems,
        {
          client_service_id: service.id,
          template_id: service.template_id || null,
          name: buildPrefixedName(service.name || '', service.service_type, service.billing_period),
          description: service.description || '',
          features: Array.isArray(service.features) ? service.features : [],
          quantity: Number(service.quantity || 1),
          unit_price: Number(service.price || 0),
          vat_rate: Number(service.vat_rate || 24),
          billing_period: service.billing_period || 'one_time',
          service_type: service.service_type || null,
        },
      ];
      return nextItems;
    });
  };

  const handleRemoveClientService = (serviceId) => {
    updateManualItems((manualItems) => manualItems.filter((item) => item.client_service_id !== serviceId));
  };

  const validateStep = () => {
    const errors = {};
    if (activeStep === 0) {
      // Either client_id must be selected OR custom fields must be filled
      if (!formData.client_id) {
        if (!formData.company_info.full_name || !formData.company_info.email || !formData.company_info.phone) {
          errors.client_or_custom = 'Επιλέξτε πελάτη ή συμπληρώστε ονοματεπώνυμο, email και τηλέφωνο';
        }
      }
    }
    if (activeStep === 1) {
      const totalItems = formData.services.length + formData.manualItems.filter((item) => item.name).length;
      if (totalItems === 0) {
        errors.services = 'Προσθέστε τουλάχιστον μία υπηρεσία ή γραμμή';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (!validateStep()) {
      return;
    }
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
    setErrorMessage('');
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
    setErrorMessage('');
  };

  const handleSubmit = async () => {
    if (!validateStep()) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    const payload = {
      client_id: formData.client_id ? Number(formData.client_id) : null,
      project_id: formData.project_id ? Number(formData.project_id) : null,
      package_id: formData.package_id ? Number(formData.package_id) : null,
      title: formData.title || undefined,
      description: formData.description || undefined,
      logo_url: formData.logo_url || undefined,
      payment_terms: Number(formData.payment_terms || 0),
      delivery_days: Number(formData.delivery_days || 0),
      validity_months: Number(formData.validity_months || 0),
      company_info: {
        ...formData.company_info,
        payment_terms: Number(formData.payment_terms || 0),
        delivery_days: Number(formData.delivery_days || 0),
        validity_months: Number(formData.validity_months || 0),
      },
      quote_settings: {
        payment_terms: Number(formData.payment_terms || 0),
        delivery_days: Number(formData.delivery_days || 0),
        validity_months: Number(formData.validity_months || 0),
      },
      services: formData.services.map((service, index) => ({
        template_id: service.template_id,
        quantity: Number(service.quantity || 1),
        sort_order: index,
        name: service.name,
        description: service.description,
        unit_price: Number(service.unit_price || 0),
        vat_rate: Number(service.vat_rate || 0),
        billing_period: service.billing_period,
        features: service.features || [],
      })),
      quote_items: formData.manualItems
        .map((item, index) => ({
          template_id: item.template_id || null,
          name: item.name,
          description: item.description,
          features: item.features || [],
          quantity: Number(item.quantity || 1),
          unit_price: Number(item.unit_price || 0),
          vat_rate: Number(item.vat_rate || 0),
          billing_period: item.billing_period || 'one_time',
          sort_order: formData.services.length + index,
        }))
        .filter((item) => item.name),
      custom_sections: [],
    };

    const result = initialQuote?.id 
      ? await quoteService.update(initialQuote.id, payload)
      : await quoteService.create(payload);
    setIsSubmitting(false);

    if (result.success) {
      if (onSubmit) {
        onSubmit(result.data);
      }
      onClose();
    } else {
      const validationDetail = result.errorResponse?.detail;
      if (Array.isArray(validationDetail)) {
        const mappedErrors = {};
        validationDetail.forEach((err) => {
          const path = err.loc?.join('.') || 'form';
          mappedErrors[path] = err.msg;
        });
        setValidationErrors(mappedErrors);
      }
      setErrorMessage(result.error || (initialQuote?.id ? 'Απέτυχε η ενημέρωση προσφοράς' : 'Απέτυχε η δημιουργία προσφοράς'));
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <ClientSelect
                name="client_id"
                value={formData.client_id}
                onChange={(field, value) => {
                  logger.debug('QuoteForm: client changed', { value });
                  setFormData((prev) => ({
                    ...prev,
                    client_id: value ? Number(value) : '',
                    project_id: value ? prev.project_id : '',
                  }));
                }}
                label="Πελάτης"
                error={Boolean(validationErrors.client_or_custom)}
                helperText={validationErrors.client_or_custom || 'Επιλέξτε πελάτη ή συμπληρώστε τα custom στοιχεία παρακάτω'}
              />
            </Grid>
            <Grid item xs={12}>
              <ProjectSelect
                name="project_id"
                clientId={formData.client_id}
                value={formData.project_id}
                onChange={(field, value) => {
                  logger.debug('QuoteForm: project changed', { value });
                  setFormData((prev) => ({
                    ...prev,
                    project_id: value ? Number(value) : '',
                  }));
                }}
                label="Έργο"
                disabled={!formData.client_id}
                helperText={!formData.client_id ? 'Επιλέξτε πρώτα πελάτη για να φορτώσουν τα έργα' : ''}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                value={packages.find((pkg) => String(pkg.id) === String(formData.package_id)) || null}
                options={packages}
                onChange={(event, option) => setFormData((prev) => ({ ...prev, package_id: option ? option.id : '' }))}
                getOptionLabel={(option) => option?.name || ''}
                loading={isLoadingPackages}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Πακέτο Προσφοράς"
                    placeholder="Επιλέξτε πακέτο για αυτόματες ενότητες"
                    onChange={(event) => setPackageSearch(event.target.value)}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingPackages ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Τίτλος"
                fullWidth
                value={formData.title}
                onChange={handleChange('title')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Λογότυπο (URL)"
                fullWidth
                value={formData.logo_url}
                onChange={handleChange('logo_url')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Περιγραφή"
                fullWidth
                multiline
                minRows={3}
                value={formData.description}
                onChange={handleChange('description')}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Προκαταβολή (%)"
                type="number"
                fullWidth
                value={formData.payment_terms}
                onChange={handleChange('payment_terms')}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Ημέρες Παράδοσης"
                type="number"
                fullWidth
                value={formData.delivery_days}
                onChange={handleChange('delivery_days')}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Ισχύς (μήνες)"
                type="number"
                fullWidth
                value={formData.validity_months}
                onChange={handleChange('validity_months')}
              />
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle1" fontWeight={600}>
                  Στοιχεία Εταιρείας
                </Typography>
                <IconButton onClick={() => setExpandedCompanyInfo((prev) => !prev)}>
                  {expandedCompanyInfo ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              <Collapse in={expandedCompanyInfo}>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {!formData.client_id && (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                          Στοιχεία πελάτη (για μη καταχωρημένους πελάτες)
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Ονοματεπώνυμο"
                          fullWidth
                          value={formData.company_info.full_name}
                          onChange={handleCompanyInfoChange('full_name')}
                          required={!formData.client_id}
                          error={!formData.client_id && !formData.company_info.full_name && Boolean(validationErrors.client_or_custom)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Email"
                          fullWidth
                          type="email"
                          value={formData.company_info.email}
                          onChange={handleCompanyInfoChange('email')}
                          required={!formData.client_id}
                          error={!formData.client_id && !formData.company_info.email && Boolean(validationErrors.client_or_custom)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Τηλέφωνο"
                          fullWidth
                          value={formData.company_info.phone}
                          onChange={handleCompanyInfoChange('phone')}
                          required={!formData.client_id}
                          error={!formData.client_id && !formData.company_info.phone && Boolean(validationErrors.client_or_custom)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Επωνυμία"
                      fullWidth
                      value={formData.company_info.company_name}
                      onChange={handleCompanyInfoChange('company_name')}
                    />
                  </Grid>
                  {formData.client_id && (
                    <>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Email"
                          fullWidth
                          value={formData.company_info.email}
                          onChange={handleCompanyInfoChange('email')}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Τηλέφωνο"
                          fullWidth
                          value={formData.company_info.phone}
                          onChange={handleCompanyInfoChange('phone')}
                        />
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Website"
                      fullWidth
                      value={formData.company_info.website}
                      onChange={handleCompanyInfoChange('website')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Τράπεζα"
                      fullWidth
                      value={formData.company_info.bank_name}
                      onChange={handleCompanyInfoChange('bank_name')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="IBAN"
                      fullWidth
                      value={formData.company_info.iban}
                      onChange={handleCompanyInfoChange('iban')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Δικαιούχος"
                      fullWidth
                      value={formData.company_info.account_holder}
                      onChange={handleCompanyInfoChange('account_holder')}
                    />
                  </Grid>
                </Grid>
              </Collapse>
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Accordion
              expanded={sectionsExpanded.templates}
              onChange={handleToggleSection('templates')}
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Typography variant="subtitle1" fontWeight={600}>Πρόσθεσε υπηρεσίες από πρότυπα</Typography>
                  <Chip size="small" label={formData.services.length} variant="outlined" color="primary" />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Autocomplete
                  options={serviceOptions}
                  loading={isLoadingServices}
                  onChange={(event, option) => handleAddService(option)}
                  getOptionLabel={(option) => option?.name || ''}
                  groupBy={(option) => {
                    if (!option?.service_type) return 'Άλλο';
                    const typeLabel = formatServiceTypeLabel(option.service_type);
                    return typeLabel || 'Άλλο';
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Πρότυπο Υπηρεσίας"
                      placeholder="Αναζήτησε πρότυπα για προσθήκη"
                      onChange={(event) => setServiceSearch(event.target.value)}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isLoadingServices ? <CircularProgress color="inherit" size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderGroup={(params) => {
                    const count = serviceOptions.filter(opt => {
                      const optType = opt?.service_type ? formatServiceTypeLabel(opt.service_type) : 'Άλλο';
                      return optType === params.group;
                    }).length;
                    return (
                      <li key={params.key}>
                        <Box sx={{ p: 1, pt: 1.5 }}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {params.group} ({count} πρότυπα)
                          </Typography>
                        </Box>
                        <ul style={{ padding: 0, margin: 0 }}>
                          {params.children}
                        </ul>
                      </li>
                    );
                  }}
                />
                {validationErrors.services && (
                  <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                    {validationErrors.services}
                  </Typography>
                )}

                <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {formData.services.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Δεν έχουν προστεθεί υπηρεσίες από πρότυπα ακόμη.
                    </Typography>
                  ) : (
                    formData.services.map((service, index) => (
                      <Box
                        key={service.template_id}
                        sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle1" fontWeight={600}>
                            {service.name}
                          </Typography>
                          <IconButton color="error" onClick={() => removeService(index)}>
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                          <Grid item xs={12} md={6}>
                            <TextField
                              label="Τίτλος"
                              fullWidth
                              value={service.name}
                              onChange={(event) => handleServiceChange(index, 'name', event.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <TextField
                              label="Ποσότητα"
                              type="number"
                              fullWidth
                              value={service.quantity}
                              onChange={(event) => handleServiceChange(index, 'quantity', Number(event.target.value))}
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <TextField
                              label="Τιμή (€)"
                              type="number"
                              fullWidth
                              value={service.unit_price}
                              onChange={(event) => handleServiceChange(index, 'unit_price', Number(event.target.value))}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              label="Περιγραφή"
                              fullWidth
                              multiline
                              minRows={2}
                              value={service.description}
                              onChange={(event) => handleServiceChange(index, 'description', event.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              label="Χαρακτηριστικά (ένα ανά γραμμή)"
                              fullWidth
                              multiline
                              minRows={3}
                              value={(service.features || []).join('\n')}
                              onChange={(event) => handleServiceChange(index, 'features', event.target.value)}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    ))
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={sectionsExpanded.clientServices}
              onChange={handleToggleSection('clientServices')}
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Typography variant="subtitle1" fontWeight={600}>Πρόσθεσε υπηρεσίες από υπηρεσίες πελάτη</Typography>
                  <Chip
                    size="small"
                    label={selectedClientServicesCount}
                    variant="outlined"
                    color="secondary"
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {!formData.client_id ? (
                  <Alert severity="info">Επιλέξτε πρώτα έναν πελάτη για να φορτώσουν οι υπηρεσίες.</Alert>
                ) : (
                  <>
                    <TextField
                      label="Αναζήτηση υπηρεσιών πελάτη"
                      placeholder="Πληκτρολόγησε τίτλο ή λέξη κλειδί"
                      fullWidth
                      size="small"
                      value={clientServiceSearch}
                      onChange={(event) => setClientServiceSearch(event.target.value)}
                      InputProps={{
                        endAdornment: isLoadingClientServices ? <CircularProgress color="inherit" size={18} /> : null,
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Οι επιλεγμένες υπηρεσίες προστίθενται ως γραμμές στην προσφορά και μπορούν να επεξεργαστούν.
                    </Typography>
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {isLoadingClientServices ? (
                        <Box display="flex" justifyContent="center" py={3}>
                          <CircularProgress />
                        </Box>
                      ) : clientServiceOptions.length === 0 ? (
                        <Alert severity="info">
                          Δεν βρέθηκαν υπηρεσίες για τον συγκεκριμένο πελάτη με τα τρέχοντα φίλτρα.
                        </Alert>
                      ) : (
                        clientServiceOptions.map((service) => {
                          const isSelected = formData.manualItems.some(
                            (item) => item.client_service_id === service.id,
                          );
                          const totalPrice = quoteService.formatCurrency(
                            Number(service.price || 0) * Number(service.quantity || 1),
                          );
                          return (
                            <Box
                              key={service.id}
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: isSelected ? 'primary.main' : 'divider',
                                backgroundColor: isSelected ? 'action.hover' : 'background.paper',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                gap: 2,
                                flexWrap: 'wrap',
                              }}
                            >
                              <Box sx={{ flex: 1, minWidth: 240 }}>
                                <Typography variant="subtitle2" fontWeight={600}>
                                  {service.name}
                                </Typography>
                                {service.description && (
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    {service.description}
                                  </Typography>
                                )}
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                                  <Chip
                                    label={`Ποσότητα: ${service.quantity || 1}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                  <Chip
                                    label={`Τιμή: ${quoteService.formatCurrency(Number(service.price || 0))}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                  <Chip
                                    label={`Σύνολο: ${totalPrice}`}
                                    size="small"
                                    color="primary"
                                  />
                                  {service.billing_period && (
                                    <Chip
                                      label={billingPeriodLabels[service.billing_period] || service.billing_period}
                                      size="small"
                                      color="info"
                                      variant="outlined"
                                    />
                                  )}
                                </Box>
                              </Box>
                              <Button
                                variant={isSelected ? 'outlined' : 'contained'}
                                color={isSelected ? 'secondary' : 'primary'}
                                startIcon={isSelected ? <DeleteIcon /> : <AddIcon />}
                                onClick={() =>
                                  isSelected
                                    ? handleRemoveClientService(service.id)
                                    : handleAddClientService(service)
                                }
                              >
                                {isSelected ? 'Αφαίρεση' : 'Προσθήκη'}
                              </Button>
                            </Box>
                          );
                        })
                      )}
                    </Box>
                  </>
                )}
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={sectionsExpanded.manual}
              onChange={handleToggleSection('manual')}
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Typography variant="subtitle1" fontWeight={600}>Προσαρμοσμένες γραμμές</Typography>
                  <Chip
                    size="small"
                    label={selectedCounts.custom}
                    variant="outlined"
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Γραμμές Προσφοράς
                  </Typography>
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={addManualItem}>
                    Προσθήκη γραμμής
                  </Button>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {formData.manualItems.map((item, index) => {
                    const canRemove = formData.manualItems.length > 1 || item.client_service_id;
                    return (
                      <Box
                        key={`manual-${index}`}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: '1px dashed',
                          borderColor: item.client_service_id ? 'primary.light' : 'divider',
                          backgroundColor: item.client_service_id ? 'rgba(139,92,246,0.05)' : 'transparent',
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Typography variant="subtitle2" fontWeight={600}>
                              Γραμμή #{index + 1}
                            </Typography>
                            {item.client_service_id && (
                              <Chip label="Από υπηρεσίες πελάτη" size="small" color="secondary" variant="outlined" />
                            )}
                          </Box>
                          {canRemove && (
                            <IconButton color="error" onClick={() => removeManualItem(index)}>
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                          <Grid item xs={12} md={6}>
                            <TextField
                              label="Τίτλος"
                              fullWidth
                              value={item.name}
                              onChange={(event) => handleManualItemChange(index, 'name', event.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <TextField
                              label="Ποσότητα"
                              type="number"
                              fullWidth
                              value={item.quantity}
                              onChange={(event) => handleManualItemChange(index, 'quantity', Number(event.target.value))}
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <TextField
                              label="Τιμή (€)"
                              type="number"
                              fullWidth
                              value={item.unit_price}
                              onChange={(event) => handleManualItemChange(index, 'unit_price', Number(event.target.value))}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              label="Περιγραφή"
                              fullWidth
                              multiline
                              minRows={2}
                              value={item.description}
                              onChange={(event) => handleManualItemChange(index, 'description', event.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              label="Χαρακτηριστικά (ένα ανά γραμμή)"
                              fullWidth
                              multiline
                              minRows={3}
                              value={(item.features || []).join('\n')}
                              onChange={(event) => handleManualItemChange(index, 'features', event.target.value)}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    );
                  })}
                </Box>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={sectionsExpanded.selected}
              onChange={handleToggleSection('selected')}
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Typography variant="subtitle1" fontWeight={600}>Επιλεγμένες υπηρεσίες</Typography>
                  <Chip size="small" label={selectedServices.length} variant="outlined" color="primary" />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {selectedServices.length === 0 ? (
                  <Alert severity="info">Δεν έχουν επιλεγεί υπηρεσίες ακόμη.</Alert>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {selectedServices.map((entry, position) => {
                      const totalPrice = quoteService.formatCurrency(entry.unitPrice * entry.quantity);
                      const isSubscription = entry.billingPeriod && entry.billingPeriod !== 'one_time';

                      return (
                        <Box
                          key={entry.key}
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            backgroundColor: 'background.paper',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              gap: 1,
                              flexWrap: 'wrap',
                            }}
                          >
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                              <Typography variant="subtitle1" fontWeight={600}>
                                {position + 1}. {entry.name}
                              </Typography>
                              <Chip label={entry.sourceLabel} size="small" />
                            </Box>
                            <IconButton
                              color="error"
                              onClick={() =>
                                entry.type === 'template'
                                  ? removeService(entry.index)
                                  : removeManualItem(entry.index)
                              }
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>

                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                            {entry.serviceTypeLabel && (
                              <Chip label={entry.serviceTypeLabel} size="small" variant="outlined" />
                            )}
                            <Chip label={`Ποσότητα: ${entry.quantity}`} size="small" variant="outlined" />
                            <Chip
                              label={`Τιμή μον.: ${quoteService.formatCurrency(entry.unitPrice)}`}
                              size="small"
                              variant="outlined"
                            />
                            <Chip label={`Σύνολο: ${totalPrice}`} size="small" color="primary" />
                            {isSubscription && (
                              <Chip
                                label={billingPeriodLabels[entry.billingPeriod] || entry.billingPeriod}
                                size="small"
                                color="info"
                                variant="outlined"
                              />
                            )}
                          </Box>

                          {entry.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              {entry.description}
                            </Typography>
                          )}

                          {entry.features && entry.features.length > 0 && (
                            <Box component="ul" sx={{ pl: 2, mt: 1, mb: 0 }}>
                              {entry.features.map((feature, featureIndex) => (
                                <li key={`${entry.key}-feature-${featureIndex}`}>
                                  <Typography variant="body2" color="text.secondary">
                                    {feature}
                                  </Typography>
                                </li>
                              ))}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          </Box>
        );
      case 2:
        return (
          <Box sx={{ mt: 1 }}>
            <Typography variant="h6" fontWeight={700}>
              Ανασκόπηση
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {initialQuote?.id 
                ? 'Ελέγξτε τα στοιχεία πριν την οριστική ενημέρωση της προσφοράς.'
                : 'Ελέγξτε τα στοιχεία πριν την οριστική δημιουργία της προσφοράς.'}
            </Typography>

            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Πελάτης / Έργο
              </Typography>
              <Typography variant="body2">
                Πελάτης: {reviewData.clientName || (formData.client_id ? `ID: ${formData.client_id}` : '—')}
              </Typography>
              {formData.project_id && (
                <Typography variant="body2">
                  Έργο: {reviewData.projectName || `ID: ${formData.project_id}`}
                </Typography>
              )}
              {formData.package_id && (
                <Typography variant="body2">
                  Πακέτο: {reviewData.packageName || `ID: ${formData.package_id}`}
                </Typography>
              )}
              {!formData.client_id && formData.company_info?.full_name && (
                <>
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                    Email: {formData.company_info.email || '—'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                    Τηλέφωνο: {formData.company_info.phone || '—'}
                  </Typography>
                </>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                Συνολικές Υπηρεσίες
              </Typography>
              <Typography variant="body2">
                Από πρότυπα: {selectedCounts.templates} • Από υπηρεσίες πελάτη: {selectedCounts.client} • Προσαρμοσμένες γραμμές: {selectedCounts.custom}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                Οικονομικά
              </Typography>
              <Typography variant="body2">Υποσύνολο: {totals.subtotal}</Typography>
              <Typography variant="body2">ΦΠΑ: {totals.vat}</Typography>
              <Typography variant="h6" fontWeight={700} sx={{ mt: 1 }}>
                Σύνολο: {totals.total}
              </Typography>
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => (!isSubmitting ? onClose() : null)}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 720 }, maxWidth: '100%' },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {initialQuote?.id ? 'Επεξεργασία Προσφοράς' : 'Νέα Προσφορά'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {initialQuote?.id 
                ? `Επεξεργάσου την προσφορά ${initialQuote.quote_number || ''}`.trim()
                : 'Δημιούργησε προσφορά επιλέγοντας πελάτη, πακέτο και υπηρεσίες.'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} disabled={isSubmitting}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 3, pt: 2 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {errorMessage && (
          <Box sx={{ px: 3 }}>
            <Alert severity="error" onClose={() => setErrorMessage('')}>
              {errorMessage}
            </Alert>
          </Box>
        )}

        <Box sx={{ px: 3, pb: 3, flex: 1, overflowY: 'auto' }}>
          {renderStepContent()}
        </Box>

        <Divider />

        <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Βήμα {activeStep + 1} από {steps.length}
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Button onClick={onClose} disabled={isSubmitting}>
              Ακύρωση
            </Button>
            {activeStep > 0 && (
              <Button variant="outlined" onClick={handleBack} disabled={isSubmitting}>
                Πίσω
              </Button>
            )}
            {activeStep < steps.length - 1 ? (
              <Button variant="contained" onClick={handleNext} disabled={isSubmitting}>
                Συνέχεια
              </Button>
            ) : (
              <Tooltip title={initialQuote?.id ? "Ενημέρωση προσφοράς" : "Δημιουργία προσφοράς"}>
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    startIcon={<SaveIcon />}
                  >
                    {initialQuote?.id ? 'Ενημέρωση' : 'Αποθήκευση'}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default QuoteFormDrawer;

